import React, { useState, useRef } from "react";
import { Upload, FileSpreadsheet, CheckCircle2, XCircle, Download, RotateCcw } from "lucide-react";
import { API, api } from "../lib/api";
import { useData } from "../lib/data-context";

const REQUIRED = [
  "lote", "linea", "denominacion", "tipologia_vehiculo", "tamano_vehiculo",
  "veh_km_anuales", "consumo_kwh_km_base", "consumo_kwh_km_conservador",
  "consumo_anual_estimado", "viabilidad"
];

export default function CargaDatos() {
  const { refresh } = useData();
  const fileRef = useRef();
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState({ state: "idle" });
  const [replace, setReplace] = useState(true);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setStatus({ state: "idle" });
  };

  const onUpload = async () => {
    if (!file) return;
    setStatus({ state: "uploading" });
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await fetch(`${API}/upload?replace=${replace}`, {
        method: "POST",
        body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || "Error");
      setStatus({ state: "success", insertados: data.insertados });
      await refresh();
    } catch (e) {
      setStatus({ state: "error", message: e.message });
    }
  };

  const onReset = async () => {
    if (!window.confirm("¿Restaurar el dataset original del anteproyecto + lotes simulados?")) return;
    setStatus({ state: "uploading" });
    try {
      await api.post("/reset");
      setStatus({ state: "success", insertados: "Dataset original" });
      await refresh();
    } catch (e) {
      setStatus({ state: "error", message: e.message });
    }
  };

  const downloadTemplate = () => {
    const headers = [...REQUIRED, "tipo_servicio", "clase_vehiculo", "etiqueta_ambiental", "longitud_km", "expediciones_anuales", "observaciones"];
    const sample = [
      "CM-11", "151", "Madrid (Plaza Castilla) - Alcobendas", "Clase II 12 m", "12 m",
      "230000", "1.32", "1.55", "303600", "Alta",
      "Urbano", "Clase II", "0 emisiones", "18.4", "12500", "Servicio compatible carga cochera",
    ];
    const csv = headers.join(";") + "\n" + sample.join(";");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "plantilla_crtm.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="carga-page">
      <div>
        <div className="text-xs font-semibold tracking-widest text-gray-500 uppercase">Carga de datos</div>
        <h1 className="section-title mt-1">Importar resultados del estudio</h1>
        <p className="section-sub">Sube un archivo CSV o Excel con la tabla de líneas analizadas. Se valida la presencia de campos obligatorios.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 crtm-card p-6">
          <div
            onClick={() => fileRef.current?.click()}
            className="border-2 border-dashed border-gray-300 rounded-xl p-10 text-center cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition"
            data-testid="dropzone"
          >
            <Upload size={32} className="mx-auto text-gray-500" />
            <div className="mt-3 font-semibold text-gray-900">
              {file ? file.name : "Seleccionar archivo CSV o Excel"}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              .csv, .xlsx (máximo recomendado 5 MB)
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              onChange={onPick}
              data-testid="file-input"
            />
          </div>

          <div className="mt-4 flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={replace}
                onChange={(e) => setReplace(e.target.checked)}
                data-testid="checkbox-replace"
              />
              Sustituir datos existentes
            </label>
          </div>

          <div className="mt-5 flex items-center gap-2 flex-wrap">
            <button className="btn-primary" onClick={onUpload} disabled={!file || status.state === "uploading"} data-testid="btn-upload">
              <Upload size={14} /> {status.state === "uploading" ? "Cargando…" : "Subir y procesar"}
            </button>
            <button className="btn-secondary" onClick={downloadTemplate} data-testid="btn-template">
              <Download size={14} /> Plantilla CSV
            </button>
            <button className="btn-secondary" onClick={onReset} data-testid="btn-reset-dataset">
              <RotateCcw size={14} /> Restaurar dataset original
            </button>
          </div>

          {status.state === "success" && (
            <div className="mt-5 flex items-center gap-2 bg-green-50 border border-green-200 text-green-900 rounded-lg p-3" data-testid="upload-success">
              <CheckCircle2 size={18} />
              <div className="text-sm">
                Carga completada — <strong>{status.insertados}</strong> registros incorporados.
              </div>
            </div>
          )}
          {status.state === "error" && (
            <div className="mt-5 flex items-start gap-2 bg-red-50 border border-red-200 text-red-900 rounded-lg p-3" data-testid="upload-error">
              <XCircle size={18} className="mt-0.5" />
              <div className="text-sm whitespace-pre-wrap">{status.message}</div>
            </div>
          )}
        </div>

        <div className="crtm-card p-6">
          <div className="flex items-center gap-2 mb-3">
            <FileSpreadsheet size={16} />
            <h3 className="font-heading font-semibold text-[15px]">Campos obligatorios</h3>
          </div>
          <ul className="space-y-1.5 text-sm text-gray-700 font-mono">
            {REQUIRED.map((r) => (
              <li key={r} className="flex items-center gap-2">
                <span className="w-1 h-1 rounded-full bg-gray-400" /> {r}
              </li>
            ))}
          </ul>
          <div className="mt-4 text-xs text-gray-500 leading-relaxed">
            Campos opcionales: <code>tipo_servicio</code>, <code>clase_vehiculo</code>, <code>etiqueta_ambiental</code>, <code>longitud_km</code>, <code>expediciones_anuales</code>, <code>observaciones</code>.
            Si <em>consumo_anual_estimado</em> no se aporta, se calcula como veh_km × kWh/km base.
          </div>
        </div>
      </div>
    </div>
  );
}
